import { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Autocomplete, SxProps, TextField } from '@mui/material';
import ApiService from '../../../ApiService';
import { OemProgram } from '../../../types/DspEntity';
import { OemProgramCriteria } from '../../../types/Criteria/OemProgramCriteria';

interface OemProgramAutocompleteProps {
    value: number | number[] | null;
    onChange: (value: OemProgram | OemProgram[] | null) => void;
    multiple?: boolean;
    disabled?: boolean;
    sx?: SxProps;
    label?: string;
    options?: OemProgram[];
    criteria?: OemProgramCriteria;
}

const OemProgramAutocomplete: FC<OemProgramAutocompleteProps> = ({
    value,
    onChange,
    label = 'Select OEM',
    multiple = false,
    disabled = false,
    sx = {},
    options = undefined,
    criteria = {},
}) => {
    const { data } = useQuery({
        queryKey: ['oemPrograms', criteria],
        queryFn: async () => {
            const { data } = await ApiService.getFilteredOemPrograms(criteria, { page: 0, size: -1 });
            return data.content;
        },
        enabled: options === undefined,
    });

    const allOptions = options ?? data ?? [];

    const selectedOptions = multiple
        ? allOptions.filter((oem) => (Array.isArray(value) ? value.includes(oem.id) : false))
        : allOptions.find((oem) => oem.id === value) ?? null;

    return (
        <Autocomplete
            size="small"
            multiple={multiple}
            disabled={disabled}
            value={selectedOptions}
            onChange={(_, newValue) => onChange(newValue)}
            options={allOptions || []}
            sx={sx}
            getOptionLabel={(option: OemProgram) => option.oemProgram}
            getOptionDisabled={(option: OemProgram) => !!option?.disabled}
            isOptionEqualToValue={(option: OemProgram, value: OemProgram) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label={label} />}
        />
    );
};

export default OemProgramAutocomplete;
